import type {
  ToolInvocationPart,
  TypedToolPart,
  MCPToolInvocationPart,
  UnifiedToolPart,
  UnifiedToolState,
  ToolCategory,
} from '../types/chat';

function inferCategory(toolName: string, serverName?: string): ToolCategory {
  // Browser tools take priority even when served via MCP (e.g. Playwright)
  if (toolName.startsWith('browser_')) return 'browser';
  if (toolName.startsWith('delegate_to_')) return 'a2a';
  if (serverName) return 'mcp';
  return 'agentscope';
}

function mapTypedState(state: string): UnifiedToolState {
  switch (state) {
    case 'input-streaming':
      return 'pending';
    case 'input-available':
      return 'running';
    case 'output-available':
      return 'completed';
    case 'output-error':
      return 'failed';
    default:
      return 'running';
  }
}

export function normalizeToolPart(
  part: ToolInvocationPart | TypedToolPart | MCPToolInvocationPart,
  extra?: {
    startedAt?: number;
    completedAt?: number;
    durationMs?: number;
    category?: string;
    serverName?: string;
    parentToolCallId?: string;
  },
): UnifiedToolPart {
  if (part.type === 'tool-invocation') {
    const p = part as ToolInvocationPart;
    const state: UnifiedToolState =
      p.state === 'running' ? 'running' : p.state === 'completed' ? 'completed' : 'failed';
    return {
      type: 'unified-tool',
      toolCallId: p.toolCallId,
      toolName: p.toolName,
      state,
      category: (extra?.category as ToolCategory) ?? inferCategory(p.toolName),
      input: Object.keys(p.args).length > 0 ? p.args : undefined,
      output: p.result,
      startedAt: extra?.startedAt,
      completedAt: extra?.completedAt,
      durationMs: extra?.durationMs,
      serverName: extra?.serverName,
      parentToolCallId: extra?.parentToolCallId,
    };
  }

  if (part.type === 'mcp-tool-invocation') {
    const p = part as MCPToolInvocationPart;
    return {
      type: 'unified-tool',
      toolCallId: p.toolCallId,
      toolName: p.toolName,
      state: p.state as UnifiedToolState,
      category: inferCategory(p.toolName, p.serverName),
      serverName: p.serverName,
      input: Object.keys(p.args).length > 0 ? p.args : undefined,
      output: p.result,
      errorText: p.errorText,
      startedAt: p.startedAt,
      completedAt: p.completedAt,
      durationMs: p.duration,
    };
  }

  // TypedToolPart (tool-<name>)
  const p = part as TypedToolPart;
  return {
    type: 'unified-tool',
    toolCallId: p.toolCallId,
    toolName: p.toolName,
    state: mapTypedState(p.state),
    category: (extra?.category as ToolCategory) ?? inferCategory(p.toolName, extra?.serverName),
    serverName: extra?.serverName,
    parentToolCallId: extra?.parentToolCallId,
    input: p.input,
    output: p.output,
    errorText: p.errorText,
    startedAt: extra?.startedAt,
    completedAt: extra?.completedAt,
    durationMs: extra?.durationMs,
  };
}
